import { cn } from "@/lib/utils"
import {
  ContentItemCreationMode,
  type ContentItem,
} from "@/shared/api/generated"
import styles from "./styles.module.scss"
import { Button } from "@/components/ui/button"
import { useEffect, useRef, useState } from "react"
import {
  Archive,
  Download,
  EllipsisVertical,
  ExternalLink,
  Pause,
  Pencil,
  Play,
  Puzzle,
  Trash,
} from "lucide-react"
import {
  indexDBService,
  useArchiveContentItem,
  useDeleteContentItem,
  useDownloadContentItem,
  useModalStore,
  useAutoTranslation,
  type UserLibraryViewMode,
} from "@/entities"
import { activePlayer } from "@/entities/audio-engine"
import { Skeleton } from "@/components/ui/skeleton"
import { formatAudioTime, formatCreatedAt } from "@/shared/utils"
import { Progress } from "@/components/ui/progress"
import {
  LANG_KEYS,
  MOBILE_TRANSLATION_KEYS,
  ROUTER_PATHS,
} from "@/shared/constants"
import { useNavigate } from "react-router-dom"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Modal } from "@/shared/types"
import { RenameContentModal } from "../modals"
import { Spinner } from "@/components/ui/spinner"
import { observer } from "mobx-react-lite"
interface Props extends ContentItem {
  className?: string
  libraryViewMode: UserLibraryViewMode

  isFirstIdx?: boolean
  isLastIdx?: boolean
}

const DropdownMenuActions = ({
  id,
  audioUrl,
  title,
  sourceUrl,
  wrapperClassName,
  wrapperOpenClassName,
}: {
  id: string
  audioUrl: string
  title: string
  /** SELECTION items: the web page the text was selected on */
  sourceUrl?: string | null
  wrapperClassName?: string
  wrapperOpenClassName?: string
}) => {
  const { t } = useAutoTranslation()
  const openModal = useModalStore(s => s.openModal)
  const { handleArchive: doArchive, isLoading: isArchivingLoading } =
    useArchiveContentItem()
  const { downloadItem } = useDownloadContentItem(audioUrl, title + ".mp3")
  const { handleDelete: doDelete, isLoading: isDeletingLoading } =
    useDeleteContentItem("library")
  const [open, setOpen] = useState(false)

  const [side, setSide] = useState<"top" | "right" | "bottom" | "left">(
    window.innerWidth > 1200 ? "bottom" : "left"
  )

  const handleRename = () => {
    openModal(
      Modal.RENAME_CONTENT,
      <RenameContentModal id={id} title={title} />
    )
  }

  const handleArchive = () => {
    doArchive(id)
  }

  const handleDelete = () => {
    doDelete(id)
  }

  useEffect(() => {
    const handler = () => {
      setSide(window.innerWidth > 1200 ? "bottom" : "left")
    }
    window.addEventListener("resize", handler)
    return () => {
      window.removeEventListener("resize", handler)
    }
  }, [window.innerWidth])

  return (
    <div className={cn(wrapperClassName, open && wrapperOpenClassName)}>
      <DropdownMenu
        open={isArchivingLoading || isDeletingLoading ? true : open}
        onOpenChange={setOpen}
        modal={false}
      >
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="icon">
            <EllipsisVertical />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side={side} align="start">
          <DropdownMenuItem onClick={handleRename}>
            <Pencil />
            {t(LANG_KEYS.RENAME)}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleArchive}>
            {isArchivingLoading ? <Spinner /> : <Archive />}
            {t(LANG_KEYS.ARCHIVE)}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleDelete}>
            {isDeletingLoading ? <Spinner /> : <Trash />}
            {t(LANG_KEYS.DELETE)}
          </DropdownMenuItem>
          {audioUrl && (
            <DropdownMenuItem onClick={downloadItem}>
              <Download />
              {t(LANG_KEYS.DOWNLOAD)}
            </DropdownMenuItem>
          )}
          {sourceUrl && (
            <DropdownMenuItem
              onClick={() => window.open(sourceUrl, "_blank", "noopener")}
            >
              <ExternalLink />
              {t(MOBILE_TRANSLATION_KEYS.OPEN_SOURCE)}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

/** Small "Created from the browser extension" chip for SELECTION items. */
const ExtensionBadge = ({ className }: { className?: string }) => {
  const { t } = useAutoTranslation()
  return (
    <span
      className={cn(
        "text-muted-foreground inline-flex max-w-full items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] leading-4",
        className
      )}
      title={t(MOBILE_TRANSLATION_KEYS.QUICK_NOTE_CREATED_IN_EXTENSION)}
    >
      <Puzzle className="h-3 w-3 shrink-0" />
      <span className="truncate">
        {t(MOBILE_TRANSLATION_KEYS.FROM_EXTENSION)}
      </span>
    </span>
  )
}

const isInitializing = false

export const LibraryItem = observer((props: Props) => {
  const {
    id,
    title,
    audioUrl,
    audioTime,
    className,
    createdAt,
    libraryViewMode,
    isFirstIdx,
    isLastIdx,
    creationMode,
    sourceUrl,
  } = props
  const isFromExtension = creationMode === ContentItemCreationMode.SELECTION

  const { isPlaying, isLoading, currentPosition, id: activeId } = activePlayer
  const navigate = useNavigate()

  const isThisActive = activeId === id
  const isThisPlaying = isThisActive && isPlaying

  const [savedPosition, setSavedPosition] = useState(0)
  const prevActiveRef = useRef(isThisActive)

  // Load saved position on mount
  useEffect(() => {
    indexDBService.getAudioPosition(id).then(setSavedPosition)
  }, [id])

  // Reload saved position from indexDB when activePlayer switches away from this item
  useEffect(() => {
    if (prevActiveRef.current && !isThisActive) {
      indexDBService.getAudioPosition(id).then(setSavedPosition)
    }
    prevActiveRef.current = isThisActive
  }, [isThisActive, id])

  const displayPosition =
    isThisActive && !isLoading ? currentPosition : savedPosition
  const isThisLoading = isThisActive && isLoading

  const handleOpenDetails = () => {
    navigate(ROUTER_PATHS.APP_LIBRARY_DETAILS.replace(":id", id))
  }

  const handlePressPlay = () => {
    activePlayer.play(props)
  }

  if (libraryViewMode === "grid") {
    return (
      <div
        className={cn(styles.item, className)}
        style={{ position: "relative" }}
      >
        {isInitializing && (
          <div className={styles.item__skeletonWrapper}>
            <Skeleton className="h-full w-full" />
          </div>
        )}

        {!isInitializing && (
          <div
            className={cn(styles.item__content, {
              [styles.item__content_disabled]: !!0,
            })}
          >
            {/* ABSOLUTE */}
            <DropdownMenuActions
              id={id}
              audioUrl={audioUrl}
              title={title}
              sourceUrl={sourceUrl}
              wrapperClassName={styles.item__dropdownMenuActions}
              wrapperOpenClassName={styles.item__dropdownMenuActions_open}
            />

            <button
              type="button"
              className={cn(styles.item__content__paper, "relative")}
              onClick={handleOpenDetails}
              title={title}
            >
              {/* Origin badge pinned to the corner so the title stays centered */}
              {isFromExtension && (
                <ExtensionBadge className="absolute top-2 left-2 max-w-[60%]" />
              )}
              <h4 className={styles.item__content__title}>{title}</h4>

              <span className={styles.item__content__createdAt}>
                {formatCreatedAt(createdAt)}
              </span>
            </button>

            <div className={styles.item__content__player}>
              <Button onClick={handlePressPlay} isLoading={isThisLoading}>
                {isThisPlaying ? <Pause /> : <Play />}
              </Button>
              <div className="flex w-full items-center gap-2">
                <h5 className="text-sm">{formatAudioTime(displayPosition)}</h5>
                <div className="flex-1">
                  <Progress
                    value={(displayPosition / audioTime) * 100}
                    max={100}
                  />
                </div>
                <h5 className="text-sm">{formatAudioTime(audioTime)}</h5>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className={cn(styles.listEl, className)}>
      {isInitializing && (
        <div className={styles.listEl__skeletonWrapper}>
          <Skeleton
            className={cn("h-full w-full rounded-none", {
              "rounded-t-[12px]": isFirstIdx,
              "rounded-b-[12px]": isLastIdx,
            })}
          />
        </div>
      )}

      {!isInitializing && (
        <div className={styles.listEl__inner}>
          <div className={styles.listEl__left}>
            <Button
              onClick={handlePressPlay}
              isLoading={isThisLoading}
              size="icon"
              variant={isThisPlaying ? "default" : "outline"}
              className="h-9 w-9 shrink-0"
            >
              {isThisPlaying ? (
                <Pause className="h-4 w-4" />
              ) : (
                <Play className="h-4 w-4" />
              )}
            </Button>
            <div className={styles.listEl__titleWrapper}>
              <div
                className={styles.listEl__title}
                title={title}
                onClick={handleOpenDetails}
              >
                {title}
                {isFromExtension && <ExtensionBadge className="ml-2" />}
              </div>
              <div className={styles.listEl__progress}>
                <span className={styles.listEl__time}>
                  {formatAudioTime(displayPosition)}
                </span>
                <div className="max-w-[200px] flex-1">
                  <Progress
                    value={(displayPosition / audioTime) * 100}
                    max={100}
                    className="h-1"
                  />
                </div>
                <span className={styles.listEl__time}>
                  {formatAudioTime(audioTime)}
                </span>
              </div>
            </div>
          </div>

          <div className={styles.listEl__right}>
            <span className="text-muted-foreground mr-3 hidden text-xs sm:block">
              {formatCreatedAt(createdAt)}
            </span>
            <DropdownMenuActions
              id={id}
              audioUrl={audioUrl}
              title={title}
              sourceUrl={sourceUrl}
              wrapperClassName={styles.listEl__dropdownMenuActions}
              wrapperOpenClassName={styles.listEl__dropdownMenuActions_open}
            />
          </div>
        </div>
      )}
    </div>
  )
})
